"use client";

import { useEffect, useState } from "react";

import Link from "next/link";
import { usePathname } from "next/navigation";

import { Accordion, AccordionItem } from "@heroui/accordion";

import type { DocsNavSection } from "@/lib/docs";
import { EventKeys } from "@/lib/observability/catalog";
import { EventPublisher } from "@/lib/observability/events";

function trackNavClick(href: string, section: string, label: string) {
  void EventPublisher.publishEvent(EventKeys.DOCS_NAVIGATION_LINK_CLICK, {
    href,
    section,
    title: label,
  });
}

function isActive(pathname: string | null, href: string): boolean {
  if (!pathname) return false;
  if (href === "/docs") {
    return pathname === "/docs";
  }

  return pathname === href || pathname.startsWith(`${href}/`);
}

interface DocsNavigationProps {
  sections: DocsNavSection[];
}

export function DocsNavigation({ sections }: DocsNavigationProps) {
  const pathname = usePathname();
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(
    () => new Set(["start", "developers"]),
  );

  useEffect(() => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);

      for (const sec of sections) {
        if (sec.items.some((i) => isActive(pathname, i.href))) {
          next.add(sec.id);
        }
      }

      return next;
    });
  }, [pathname, sections]);

  return (
    <nav
      aria-label="Documentation sections"
      className="flex max-h-[calc(100vh-5rem)] flex-col overflow-y-auto rounded-xl border border-default-200 bg-content1 p-1 shadow-sm"
      data-testid="docs-navigation"
    >
      <Accordion
        className="px-1 py-2"
        itemClasses={{
          base: "py-0",
          content: "pb-2 pt-0",
          title:
            "text-xs font-semibold uppercase tracking-wide text-default-500",
          trigger: "py-2",
        }}
        selectedKeys={expandedKeys}
        selectionMode="multiple"
        variant="splitted"
        onSelectionChange={(keys) => {
          if (keys === "all") return;
          setExpandedKeys(new Set(Array.from(keys, (k) => String(k))));
        }}
      >
        {sections.map((section) => (
          <AccordionItem
            key={section.id}
            aria-label={section.title}
            textValue={section.title}
            title={section.title}
          >
            <ul className="flex flex-col gap-0.5">
              {section.items.map((item) => {
                const active = isActive(pathname, item.href);

                return (
                  <li key={item.href}>
                    <Link
                      className={`block rounded-lg px-2 py-2 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-content1 ${
                        active
                          ? "bg-primary/15 font-medium text-primary"
                          : "text-default-700 hover:bg-default-100 hover:text-default-900"
                      } `}
                      href={item.href}
                      onClick={() =>
                        trackNavClick(item.href, section.title, item.label)
                      }
                    >
                      {item.label}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </AccordionItem>
        ))}
      </Accordion>
    </nav>
  );
}
